import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  fetchNoteQuestions,
  fetchAddNoteQuestion,
  fetchUpdateNoteQuestion,
  fetchSortNoteQuestions,
  fetchDeleteNoteQuestion,
  getNoteQuestions,
  getNoteQuestionsLoading,
} from "store/slices/processNote";
import AppColor from "utilities/AppColor";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import NoteQuestionForm from "./form/NoteQuestionForm";
import ParticipantProgressNoteList from "./ParticipantProgressNoteList";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  root: { padding: "0 0 32px" },

  // Week navigation bar
  weekNav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  weekNavTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
  },
  weekNavRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  weekLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    minWidth: 160,
    textAlign: "center",
  },
  navBtn: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "1px solid #E5E7EB",
    background: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#374151",
    "&:hover": { background: "#F3F4F6" },
  },
  thisWeekBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: AppColor.mainColor,
    border: `1px solid ${AppColor.mainColor}`,
    borderRadius: 20,
    padding: "3px 12px",
    background: "#fff",
    cursor: "pointer",
    "&:hover": { background: "#F0FDFB" },
  },

  // Day tabs
  dayTabs: {
    display: "flex",
    gap: 6,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  dayTab: {
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    "&:hover": { borderColor: AppColor.mainColor, color: AppColor.mainColor },
  },
  dayTabActive: {
    background: AppColor.mainColor,
    color: "#fff",
    border: `1px solid ${AppColor.mainColor}`,
    fontWeight: 700,
  },
  flagDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#EF4444",
    display: "inline-block",
  },

  // Search + filters
  filterRow: {
    display: "flex",
    gap: 10,
    marginBottom: 16,
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchInput: {
    flex: 1,
    minWidth: 280,
    height: 38,
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "0 14px",
    fontSize: 13,
    color: "#374151",
    outline: "none",
    "&:focus": { borderColor: AppColor.mainColor },
  },
  filterSelect: { minWidth: 140 },
  clearBtn: {
    fontSize: 12,
    color: "#6B7280",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { color: "#111827" },
  },

  // Summary stats
  statsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: 1,
    background: "#E5E7EB",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 20,
  },
  statBox: {
    background: "#fff",
    padding: "14px 16px",
    textAlign: "center",
  },
  statNumber: {
    fontSize: 22,
    fontWeight: 700,
    color: AppColor.mainColor,
  },
  statNumberRed: {
    fontSize: 22,
    fontWeight: 700,
    color: "#EF4444",
  },
  statLabel: {
    fontSize: 11,
    color: "#6B7280",
    marginTop: 2,
  },

  // Note card
  noteCard: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    background: "#fff",
    marginBottom: 10,
    padding: "14px 16px",
    position: "relative",
  },
  noteCardFlagged: {
    borderLeft: "3px solid #EF4444",
  },
  noteHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  workerRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: AppColor.mainColor,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },
  workerName: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
  },
  shiftLabel: {
    fontSize: 11,
    color: "#6B7280",
    marginTop: 1,
  },
  noteDateTime: {
    fontSize: 11,
    color: "#9CA3AF",
    whiteSpace: "nowrap",
  },

  // Flag banner
  flagBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 6,
    padding: "6px 12px",
    marginBottom: 8,
    fontSize: 12,
    color: "#B91C1C",
  },

  // Note text
  noteText: {
    fontSize: 13,
    color: "#374151",
    lineHeight: 1.6,
    marginBottom: 8,
  },

  // Tags row
  tagsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 4,
  },
  tags: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  tag: {
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500,
    background: "#F3F4F6",
    color: "#374151",
  },
  tagGood:     { background: "#D1FAE5", color: "#065F46" },
  tagAccept:   { background: "#DBEAFE", color: "#1E40AF" },
  tagReview:   { background: "#FEF3C7", color: "#92400E" },
  tagResubmit: { background: "#FEE2E2", color: "#991B1B" },
  tagFlagged:  { background: "#FEE2E2", color: "#B91C1C" },

  // Request resubmission button
  resubmitBtn: {
    fontSize: 11,
    fontWeight: 600,
    color: "#B91C1C",
    border: "1px solid #FECACA",
    borderRadius: 20,
    padding: "3px 12px",
    background: "#FEF2F2",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { background: "#FEE2E2" },
  },
  resubmitBtnSent: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    border: "1px solid #E5E7EB",
    borderRadius: 20,
    padding: "3px 12px",
    background: "#F9FAFB",
    cursor: "default",
    whiteSpace: "nowrap",
  },
  seeMoreLink: {
    fontSize: 12,
    color: AppColor.mainColor,
    cursor: "pointer",
    fontWeight: 600,
  },

  // Bottom
  showingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 16,
    fontSize: 13,
    color: "#6B7280",
  },
  loadAllBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    border: "1px solid #E5E7EB",
    borderRadius: 20,
    padding: "4px 14px",
    background: "#fff",
    cursor: "pointer",
    "&:hover": { background: "#F3F4F6" },
  },
  loadingBox: {
    padding: "40px 0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    color: "#6B7280",
    fontSize: 13,
  },
  emptyBox: {
    padding: "48px 0",
    textAlign: "center",
    color: "#9CA3AF",
    fontSize: 13,
  },
  writeNoteBtn: {
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
    background: AppColor.mainColor,
    border: "none",
    borderRadius: 20,
    padding: "7px 18px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    "&:hover": { opacity: 0.9 },
  },
  manageQuestionsBtn: {
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 20,
    padding: "6px 14px",
    cursor: "pointer",
    "&:hover": { background: "#F3F4F6" },
  },

  // Inline questions panel
  questionsPanel: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    marginBottom: 20,
    overflow: "hidden",
  },
  questionsPanelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    background: "#F9FAFB",
    borderBottom: "1px solid #E5E7EB",
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { background: "#F3F4F6" },
  },
  questionsPanelTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  questionsPanelBody: {
    padding: "14px 16px",
    background: "#fff",
  },
  qCard: {
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 8,
    background: "#FAFAFA",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },
  qBadge: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: AppColor.mainColor,
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    textAlign: "center",
    lineHeight: "22px",
    flexShrink: 0,
  },
  qText: {
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 6,
    flex: 1,
  },
  qChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    marginBottom: 5,
  },
  qChip: {
    padding: "2px 10px",
    borderRadius: 20,
    fontSize: 11,
    // background: AppColor.mainLight,
    // color: AppColor.green,
    border: "1px solid #A7F3D0",
    background: "#ECFDF5",
    color: "#065F46",
    fontWeight: 500,
  },
  qHint: {
    fontSize: 11,
    color: "#1D4ED8",
    marginBottom: 4,
  },
  qActions: {
    display: "flex",
    gap: 5,
    marginTop: 6,
  },
  qEditBtn: {
    fontSize: 11,
    padding: "3px 10px",
    borderRadius: 20,
    border: "1px solid #E5E7EB",
    background: "#fff",
    cursor: "pointer",
    color: "#374151",
    fontWeight: 500,
    "&:hover": { background: "#F3F4F6" },
  },
  qDeleteBtn: {
    fontSize: 11,
    padding: "3px 10px",
    borderRadius: 20,
    border: "1px solid #FECACA",
    background: "#FEF2F2",
    cursor: "pointer",
    color: "#B91C1C",
    fontWeight: 500,
    "&:hover": { background: "#FEE2E2" },
  },
  qGoalBadge: {
    fontSize: 10,
    padding: "2px 8px",
    borderRadius: 10,
    marginLeft: 6,
  },
  categoryCoreSupport: {
    background: "#F0FDFA",
    color: "#0F766E",
    border: "1px solid #14b8a6"
  },
  categorySocialCommunity: {
    background: "#EFF6FF",
    color: "#1E40AF",
    border: "1px solid #3b82f6"
  },
  categoryCapacityBuilding: {
    background: "#F5F3FF",
    color: "#5B21B6",
    border: "1px solid #8b5cf6"
  },
  qAddBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: AppColor.mainColor,
    border: `1px solid ${AppColor.mainColor}`,
    borderRadius: 20,
    padding: "5px 14px",
    background: "#fff",
    cursor: "pointer",
    marginTop: 4,
    "&:hover": { background: AppColor.mainLight },
  },
  qDivider: {
    borderTop: "1px solid #E5E7EB",
    margin: "12px 0",
  },
  qFormTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 10,
  },
  qFieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 3,
    display: "block",
  },
  qFieldHint: {
    fontSize: 11,
    color: "#9CA3AF",
    marginTop: 2,
    marginBottom: 8,
  },
  qChipInput: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "6px 10px",
    minHeight: 42,
    width: "100%",
    boxSizing: "border-box",
    marginBottom: 4,
  },
  qChipTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 12,
    background: AppColor.mainLight,
    color: AppColor.green,
    fontWeight: 500,
  },
  qChipClose: {
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1,
    color: AppColor.green,
  },
  qChipTextInput: {
    border: "none",
    outline: "none",
    fontSize: 12,
    color: "#374151",
    flex: 1,
    minWidth: 80,
    fontFamily: "inherit",
    background: "transparent",
  },
  qCheckRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
    fontSize: 13,
    color: "#374151",
    cursor: "pointer",
    userSelect: "none",
  },
  qFormActions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 10,
  },
  qSaveBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    background: AppColor.mainColor,
    border: "none",
    borderRadius: 20,
    padding: "6px 16px",
    cursor: "pointer",
    "&:hover": { opacity: 0.9 },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  qCancelBtn: {
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
    background: "#F3F4F6",
    border: "1px solid #E5E7EB",
    borderRadius: 20,
    padding: "6px 14px",
    cursor: "pointer",
    "&:hover": { background: "#E5E7EB" },
  },
}));


const parseQuickAnswers = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((s) => String(s).trim()).filter(Boolean);
    } catch (_) { /* fall through to CSV */ }
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const ParticipantProgressNote = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id: clientId } = useParams();

  const reduxQuestions = useSelector(getNoteQuestions);
  const questionsLoading = useSelector(getNoteQuestionsLoading);

  const [questions, setQuestions] = useState([]);
  
  useEffect(() => {
    setQuestions(reduxQuestions || []);
  }, [reduxQuestions]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const newQuestions = Array.from(questions);
    const [removed] = newQuestions.splice(sourceIndex, 1);
    newQuestions.splice(destinationIndex, 0, removed);
    setQuestions(newQuestions);

    const questionIds = newQuestions.map(q => q.id);
    dispatch(fetchSortNoteQuestions(questionIds));
  };

  // Inline questions panel state
  const [questionsExpanded, setQuestionsExpanded] = useState(false);
  const [qEditingId, setQEditingId] = useState(null);
  const [qShowForm, setQShowForm] = useState(false);
  const [qSaving, setQSaving] = useState(false);
  const [qDeleting, setQDeleting] = useState({});
  const [confirmDeleteQ, setConfirmDeleteQ] = useState(null);

  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  useEffect(() => {
    if (questionsExpanded && !questionsLoaded) {
      dispatch(fetchNoteQuestions({ client_id: clientId }));
      setQuestionsLoaded(true);
    }
  }, [questionsExpanded, dispatch, questionsLoaded, clientId]);

  // ── Question manager handlers ─────────────────────────────────────────────

  const qOpenAdd = () => {
    setQEditingId(null);
    setQShowForm(true);
  };

  const qOpenEdit = (q) => {
    setQEditingId(q.id);
    setQShowForm(true);
  };

  const qCancel = () => { setQShowForm(false); setQEditingId(null); };

  const qSubmit = async (values) => {
    setQSaving(true);
    const payload = { 
      question: values.question.trim(), 
      quickAnswers: (values.quickAnswers || []).map((s) => String(s).trim()).filter(Boolean), 
      hasDetail: values.hasDetail, 
      detailPlaceholder: values.detailPlaceholder?.trim() || null, 
      contextHint: values.contextHint?.trim() || null, 
      questionType: values.questionType,
      clientId: clientId,
    };
    if (qEditingId) await dispatch(fetchUpdateNoteQuestion({ id: qEditingId, ...payload }));
    else await dispatch(fetchAddNoteQuestion(payload));
    setQSaving(false);
    qCancel();
  };

  const qDelete = async (q) => {
    setConfirmDeleteQ(q);
  };

  const executeDeleteQ = async () => {
    if (!confirmDeleteQ) return;
    const q = confirmDeleteQ;
    setConfirmDeleteQ(null);
    setQDeleting((p) => ({ ...p, [q.id]: true }));
    await dispatch(fetchDeleteNoteQuestion({ id: q.id }));
    setQDeleting((p) => ({ ...p, [q.id]: false }));
  };

  return (
    <Box className={classes.root}>
      {/* End-of-shift note modal */}
      {/* <EndOfShiftNoteModal
        open={true}
        onClose={() => {}}
        clientId={clientId ? Number(clientId) : undefined}
        onSuccess={() => {}}
      /> */}

      {/* Top action row */}
      <Box className={classes.weekNav} style={{ marginBottom: 12 }}>
        <span className={classes.weekNavTitle}>{t("label.progressNote")}</span>
      </Box>

      {/* ── Inline questions manager ─────────────────────────────────── */}
      <Box className={classes.questionsPanel}>
        <Box className={classes.questionsPanelHeader} onClick={() => setQuestionsExpanded((v) => !v)}>
          <Box className={classes.questionsPanelTitle}>
            <span>⚙</span>
            {t("label.note_questions")}
            {questions.length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 400, color: "#6B7280" }}>
                ({questions.length} {t("label.questions")})
              </span>
            )}
          </Box>
          <span style={{ fontSize: 12, color: "#6B7280" }}>
            {questionsExpanded
              ? <><i className="fas fa-chevron-up" style={{ marginRight: 4 }} />{t("button.collapse")}</>
              : <><i className="fas fa-chevron-down" style={{ marginRight: 4 }} />{t("button.expand")}</>
            }
          </span>
        </Box>

        {questionsExpanded && (
          <Box className={classes.questionsPanelBody}>
            {questionsLoading ? (
              <Box style={{ display: "flex", alignItems: "center", gap: 8, color: "#6B7280", fontSize: 13, padding: "8px 0" }}>
                <CircularProgress size={14} style={{ color: AppColor.mainColor }} />
                {t("label.loading")}
              </Box>
            ) : (
              <>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="questions-list">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {questions.map((q, i) => (
                          <Draggable key={q.id.toString()} draggableId={q.id.toString()} index={i} isDragDisabled={qEditingId === q.id}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  marginBottom: 12,
                                }}
                              >
                                <Box className={classes.qCard} style={{ flexDirection: "column", padding: 0, boxShadow: snapshot.isDragging ? "0 5px 15px rgba(0,0,0,0.15)" : undefined }}>
                                  {qEditingId === q.id ? (
                                    /* ── Inline edit form ── */
                                    <NoteQuestionForm
                                      title={`${t("button.edit_question")} ${i + 1}`}
                                      submitLabel={t("button.save_changes")}
                                      isEditMode={true}
                                      initialData={{ 
                                        question: q.question || "", 
                                        quickAnswers: parseQuickAnswers(q.quickAnswers), 
                                        hasDetail: q.hasDetail || false, 
                                        detailPlaceholder: q.detailPlaceholder || "", 
                                        contextHint: q.contextHint || "", 
                                        questionType: q.questionType || "DEFAULT" 
                                      }}
                                      onSubmit={qSubmit}
                                      onCancel={qCancel}
                                      saving={qSaving}
                                    />
                                  ) : (
                                    /* ── Read-only view ── */
                                    <Box style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px" }}>
                                    <div {...provided.dragHandleProps} style={{ cursor: "grab", display: "flex", alignItems: "center", alignSelf: "stretch" }}>
                                      <DragIndicatorIcon style={{ color: "#9CA3AF" }} />
                                    </div>
                                    <span className={classes.qBadge}>{i + 1}</span>
                                      <Box style={{ flex: 1 }}>
                                        <Box className={classes.qText}>
                                          {q.question}
                                          {q.goalTrackerList && q.goalTrackerList.map(gt => {
                                            const getCategoryStyle = (category) => {
                                              switch (category) {
                                                case "CORE_SUPPORTS": return classes.categoryCoreSupport;
                                                case "CAPITAL_SUPPORTS": return classes.categorySocialCommunity;
                                                case "CAPACITY_BUILDING": return classes.categoryCapacityBuilding;
                                                default: return classes.categoryCoreSupport;
                                              }
                                            };
                                            return (
                                              <span key={gt.id} className={`${classes.qGoalBadge} ${getCategoryStyle(gt.category)}`}>{gt.name}</span>
                                            );
                                          })}
                                        </Box>
                                        {q.quickAnswers && (
                                          <Box className={classes.qChips}>
                                            {parseQuickAnswers(q.quickAnswers).map((c) => (
                                              <span key={c} className={classes.qChip}>{c}</span>
                                            ))}
                                          </Box>
                                        )}
                                        {q.contextHint && <Box className={classes.qHint}>💡 {q.contextHint}</Box>}
                                        <Box className={classes.qActions}>
                                          <button className={classes.qEditBtn} onClick={() => qOpenEdit(q)}>{t("button.edit")}</button>
                                          <button className={classes.qDeleteBtn} onClick={() => qDelete(q)} disabled={qDeleting[q.id]}>
                                            {qDeleting[q.id] ? "…" : t("button.delete")}
                                          </button>
                                        </Box>
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                {!qShowForm && !qEditingId && (
                  <button className={classes.qAddBtn} onClick={qOpenAdd}>+ {t("button.add_question")}</button>
                )}

                {qShowForm && !qEditingId && (
                  <>
                    {questions.length > 0 && <Box className={classes.qDivider} />}
                    <NoteQuestionForm
                      title={t("label.new_question")}
                      submitLabel={t("button.add_question")}
                      initialData={{ 
                        question: "", 
                        quickAnswers: [], 
                        hasDetail: false, 
                        detailPlaceholder: "", 
                        contextHint: "", 
                        questionType: "DEFAULT" 
                      }}
                      onSubmit={qSubmit}
                      onCancel={qCancel}
                      saving={qSaving}
                    />
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
      {/* ── End questions manager ────────────────────────────────────── */}
      <ParticipantProgressNoteList clientId={clientId} />

      {/* Confirm Delete Dialog */}
      <AppDialog
        open={Boolean(confirmDeleteQ)}
        onClose={() => setConfirmDeleteQ(null)}
        title={t("label.confirm_deletion")}
        maxWidth="sm"
      >
        <Box p={2}>
          <Box mb={2}>
            Are you sure you want to delete "{confirmDeleteQ?.question}"?
          </Box>
          <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
            <AppButton
              label={t("button.cancel")}
              onClick={() => setConfirmDeleteQ(null)}
              buttonColor="#F3F4F6"
              style={{ color: "#111827" }}
              rounder
            />
            <AppButton
              label={t("button.delete")}
              onClick={executeDeleteQ}
              buttonColor={AppColor.mainColor}
              rounder
            />
          </Box>
        </Box>
      </AppDialog>
    </Box>
  );
};

export default ParticipantProgressNote;

